import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { adminApi } from '@/lib/adminApi';
import { supabase } from '@/lib/supabase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  Users,
  TrendingUp,
  Eye,
  Hotel,
  Package,
  AlertTriangle,
  CalendarClock,
} from 'lucide-react';

interface OverviewData {
  onlineDrivers: number;
  totalDrivers: number;
  pendingConcierge: number;
  pendingPackages: number;
  packagesLast7d: number;
  impressionsLast30d: number;
  businessPinImpressions: number | null;
  teamImpressions: number | null;
  listingCount: number;
  activeSponsors: number;
  liveFestivals: number;
}

interface PendingDobData {
  count: number;
  nextGraceExpiry: string | null;
}

export default function Overview() {
  const { canAccess } = useAdminAuth();
  const canSeeRiders = canAccess('riders');
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rlsError, setRlsError] = useState(false);
  const [pendingDob, setPendingDob] = useState<PendingDobData | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const overviewData = await adminApi.getOverview();
        setData(overviewData);
        if (overviewData.rlsRecursionDetected) {
          setRlsError(true);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Fetch pending DOB count
  useEffect(() => {
    if (!canSeeRiders) return;
    async function loadPendingDob() {
      try {
        const { count, error: countErr } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'passenger')
          .is('date_of_birth', null);
        if (countErr) throw countErr;

        // Get the next grace window expiry
        const { data: nextGrace, error: graceErr } = await supabase
          .from('profiles')
          .select('dob_grace_until')
          .eq('role', 'passenger')
          .is('date_of_birth', null)
          .not('dob_grace_until', 'is', null)
          .order('dob_grace_until', { ascending: true })
          .limit(1);

        setPendingDob({
          count: count ?? 0,
          nextGraceExpiry: (!graceErr && nextGrace?.[0]?.dob_grace_until) || null,
        });
      } catch (e) {
        console.warn('[Overview] pendingDob fetch failed', e);
        setPendingDob({ count: 0, nextGraceExpiry: null });
      }
    }
    loadPendingDob();
  }, [canSeeRiders]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-28 bg-gray-800/50 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
        <p className="font-medium">Failed to load overview</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  const kpiCards = [
    {
      label: 'Online Drivers',
      area: 'drivers',
      value: `${data?.onlineDrivers || 0} / ${data?.totalDrivers || 0}`,
      icon: Users,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      subtitle: 'currently active',
    },
    {
      label: 'Pending Concierge',
      area: 'rides',
      value: String(data?.pendingConcierge || 0),
      icon: Hotel,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      subtitle: 'awaiting assignment',
    },
    {
      label: 'Packages (7d)',
      area: 'rides',
      value: String(data?.packagesLast7d || 0),
      icon: Package,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      subtitle: `${data?.pendingPackages || 0} pending`,
    },
    {
      label: 'Ad Impressions (30d)',
      area: canAccess('sponsors') ? 'sponsors' : 'reports',
      value: (data?.impressionsLast30d || 0).toLocaleString(),
      icon: Eye,
      color: 'text-pink-400',
      bg: 'bg-pink-500/10',
      subtitle:
        data?.teamImpressions != null
          ? `real riders only · ${data.teamImpressions.toLocaleString()} team views not counted`
          : `${data?.listingCount || 0} listings tracked`,
    },
    {
      label: 'Active Sponsors',
      area: 'sponsors',
      value: String(data?.activeSponsors || 0),
      icon: TrendingUp,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      subtitle: 'campaigns running',
    },
    {
      label: 'Live Festivals',
      area: 'sponsors',
      value: String(data?.liveFestivals || 0),
      icon: TrendingUp,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      subtitle: 'event takeovers',
    },
  ].filter((kpi) => canAccess(kpi.area));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Overview</h1>
        <p className="text-sm text-gray-400 mt-1">Platform health at a glance</p>
      </div>

      {/* Only show RLS warning if a real 42P17 recursion error was detected */}
      {rlsError && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-400 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              RLS Policy Recursion Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-300">
              A real infinite recursion error (42P17) was detected when querying admin tables.
              Some metrics may be unavailable. Please check your Supabase RLS policies.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pending DOB card */}
      {pendingDob && pendingDob.count > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-400 uppercase tracking-wider font-medium">Pending DOB</p>
                <p className="text-2xl font-bold text-white mt-1">{pendingDob.count}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {pendingDob.count} passenger{pendingDob.count !== 1 ? 's' : ''} will be auto-blocked when their grace window ends
                </p>
                {pendingDob.nextGraceExpiry && (
                  <p className="text-xs text-amber-400/80 mt-1">
                    Next grace expiry: {new Date(pendingDob.nextGraceExpiry).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </p>
                )}
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <CalendarClock className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {kpiCards.length === 0 && (
        <p className="text-sm text-gray-400">Use the menu on the left to open the parts of the portal you work in.</p>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card
              key={kpi.label}
              className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-colors"
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">{kpi.label}</p>
                    <p className="text-2xl font-bold text-white mt-1">{kpi.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{kpi.subtitle}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${kpi.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
